import { Box, Button, Grid, Typography, TextField as MuiTextField, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel } from "@mui/material"
import { crudGetList, ReduxState, ResourceComponentPropsWithId } from "ra-core"
import { ChangeEvent, FC, useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Content } from "@/components/content/Content"
import DeploymentInfo from "@/components/deployment/DeploymentInfo"
import DeploymentSelectionCard from "@/components/deployment/DeploymentSelectionCard"
import ResourceMonitorMetrics from "@/components/deployment/ResourceMonitorMetrics"
import Confirm from "@/components/widgets/Confirm"
import { DeploymentStatus } from "@/gen/api/deploy"
import { OdResourcePageProps } from "@/types/resource"
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import { useResourceRegister } from "@/utils/resourceRegister"
import { DeploymentDetailContextProvider, useDeploymentDetailContext, useDeploymentDetailController } from "./useDeploymentDetailContext"
import WarningIcon from '@mui/icons-material/Warning';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LinkIcon from '@mui/icons-material/Link';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Link as RouterLink } from 'react-router-dom'
import { useHasPermit } from "@/authProvider/useHasPermit"
import { MANAGE_DATABASE_DEPLOYMENTS } from "@/constants"


const DeploymentDetail: FC<ResourceComponentPropsWithId> = (props) => {
  useResourceRegister({ name: 'dbmetrics-vmcpu' })
  useResourceRegister({ name: 'dbmetrics-vmmemory' })
  useResourceRegister({ name: 'dbmetrics-vmdisk' })
  
  const {id: hId} = props
  const id = decodeHashid(hId || '')

  const deploymentListDataExists = useSelector((store:ReduxState) => {
    const ids = store.admin.resources['deployments/list']?.list.ids
    return ids && ids.length > 0
  })

  const dispatch = useDispatch()
  useEffect(() => {
    if (!deploymentListDataExists) {
      dispatch(crudGetList('deployments/list', pages(), sorts(), {}))
    }
  }, [deploymentListDataExists])

  const ctls = useDeploymentDetailController(+(id || 0))
  return (
    <DeploymentDetailContextProvider value={ctls}>
      <DeploymentDetailBody {...props} />
    </DeploymentDetailContextProvider>
  )
}


export const DeploymentDetailBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_DEPLOYMENTS])
  const { 
    currDatabase,
    deleteRequesting,
    deleteDeployment,
    startRequesting,
    startDeployment,
    stopRequesting,
    stopDeployment, 
  } = useDeploymentDetailContext()

  // delete action
  const [openDelete, setOpenDelete] = useState(false)
  const [backupOnDelete, setBackupOnDate] = useState(false)
  const [nameConfirm, setNameConfirm] = useState('')

  const nameConfirmed = !!(currDatabase && (currDatabase.name?.toLowerCase() === nameConfirm?.toLowerCase()))

  const handleDelete = () => { 
    deleteDeployment(backupOnDelete, {
      onSuccess: () => handleDeleteDialogClose()
    }) 
  }

  const handleNameInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNameConfirm(e.target.value)
  }, [])

  const handleDeleteDialogClose = () => {
    setOpenDelete(false)
    setNameConfirm('')
    setBackupOnDate(false)
  }

  const handleBackupChecked = (e:ChangeEvent<HTMLInputElement>) => {
    setBackupOnDate(e.target.checked)
  }
  
  // stop action
  const [openStop, setOpenStop] = useState(false)
  const [stopConfirm, setStopConfirm] = useState('')

  const stopConfirmed = 'yes' === stopConfirm

  const handleStop = () => { 
    stopDeployment({
      onSuccess: () => handleStopDialogClose()
    }) 
  }

  const handleStopConfirmInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setStopConfirm(e.target.value)
  }, [])

  const handleStopDialogClose = () => {
    setOpenStop(false)
    setStopConfirm('')
  }
  
  return (
    <Content 
      pageName="Deployment Detail" 
      resourceName="Deployment Detail" 
      breadcrumbs={[...(options?.breadcrumbs || []), { label: 'Deployment List', path: '/deployments/list' }]}
      mainActions={
        hasWritePermit &&
        <>
          <Button
            size="small"
            variant="outlined"
            disabled={!currDatabase || 
              (currDatabase.deploymentStatus !== DeploymentStatus.Success 
                && currDatabase.deploymentStatus !== DeploymentStatus.Warning) ||
              deleteRequesting || startRequesting || stopRequesting}
            onClick={() => setOpenStop(true)}
            startIcon={<StopIcon />}
          >
            Stop
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!currDatabase || 
              currDatabase.deploymentStatus !== DeploymentStatus.Stopped ||
              deleteRequesting || startRequesting || stopRequesting}
            onClick={() => startDeployment()}
            startIcon={<PlayArrowIcon />}
          >
            Resume
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!currDatabase || deleteRequesting || startRequesting || stopRequesting}
            onClick={() => setOpenDelete(true)}
            startIcon={<DeleteForeverIcon />}
          >
            Remove
          </Button>
        </>
      }
      secondaryActions={
        <Button
          color="primary"
          startIcon={<LinkIcon fontSize="small" />}
          component={RouterLink}
          to={genUrlWithHashid(`/databases/$0/show`, currDatabase?.databaseId || 0)}
        >
          Database Detail
        </Button>
      }
    >
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <DeploymentSelectionCard />
        </Grid>
        <Grid item xs={12}>
          <DeploymentInfo />
        </Grid>
        <Grid item xs={12}>
          <ResourceMonitorMetrics />
        </Grid>
      </Grid>
      {hasWritePermit &&
        <>
          <Confirm
            isOpen={openDelete}
            loading={deleteRequesting}
            valid={nameConfirmed}
            title={"Delete Deployment"}
            onConfirm={handleDelete}
            onClose={handleDeleteDialogClose}
            confirm="Confirm"
            cancel="Cancel"
            content={(
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WarningIcon fontSize="large" sx={{ color: 'warning.main' }}/>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 1 }}
                  >
                    Warning! Are you sure to remove this deployment "{currDatabase?.name}"?
                  </Typography>
                </Box>
                <MuiTextField
                  id="cofirm-deployment-name"
                  autoFocus
                  fullWidth
                  error={!nameConfirmed}
                  helperText={!nameConfirmed && "Enter the name of your deployment to confirm removal."}
                  size="medium"
                  label="Type Deployment Name"
                  sx={{ mb: 2, mt: 4, }}
                  onChange={handleNameInput}
                  value={nameConfirm}
                />
                <FormControl sx={{ mr: 3, mb: 3, ml: '14px' }} component="fieldset" variant="standard">
                  <FormGroup sx={{ alignItems: 'flex-start' }}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={backupOnDelete} onChange={handleBackupChecked} name="gilad" />
                      }
                      label="Backup before deletion"
                    />
                  </FormGroup>
                  <FormHelperText>The backup will comply with existing retension policy of the database.</FormHelperText>
                </FormControl>
              </Box>
            )}
          />
          <Confirm
            isOpen={openStop}
            loading={stopRequesting}
            valid={stopConfirmed}
            title={"Stop Deployment"}
            onConfirm={handleStop}
            onClose={handleStopDialogClose}
            confirm="Confirm"
            cancel="Cancel"
            content={(
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WarningIcon fontSize="large" sx={{ color: 'warning.main' }}/>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 1 }}
                  >
                    Are you sure to stop this deployment "{currDatabase?.name}" ?
                  </Typography>
                </Box>
                <MuiTextField
                  id="confirm-stop"
                  autoFocus
                  fullWidth
                  error={!stopConfirmed}
                  helperText={!stopConfirmed && "Enter 'yes' for confirmation"}
                  size="medium"
                  label="Confirm stop action"
                  sx={{ mb: 2, mt: 4, }}
                  onChange={handleStopConfirmInput}
                  value={stopConfirm}
                />
              </Box>
            )}
          />
        </>}
    </Content>
  )
}



export default DeploymentDetail;
